import {CSSProperties, useRef, useState} from 'react';
import {Panel} from '@/components/app/feedback';
import {Modal} from '@/components/app/modal';
import {ContentHints} from '@/components/app/hint';
import {ToggleField} from '@/components/app/inputs';
import {cn} from '@/lib/utils';
import {CertifiedBadge} from '../../../app/components/CertifiedBadge';
import {Icon} from '../../../app/components/Icon';
import {useConfigValue} from '../../../app/hooks/useConfigValue';
import {useGetBiobookPreferencesQuery, useSetBiobookPreferencesMutation} from '../../../app/services/biobookApi';
import {useGetBiotwinQuery} from '../../../app/services/biotwinApi';
import {getOrderedBiobookPageFileNames} from '../helpers/utils';
import {BiobookPage as IBiobookPage} from '../types';
import {defaultBiobookPreferences, defaultMechanismLabelColor} from '../utils';
import {AbbreviationLongName} from './AbbreviationLongName';
import {BiobookPageImage} from './BiobookPageImage';
import {BiobookPageGallery} from './BiobookPageGallery';
import styles from './BiobookPage.module.css';
const visibleImageCount = 3;
// Borderless icon toggles whose state shows only through the icon, as with the old PrimeReact ToggleButtons.
const toggleClassName = 'size-11 border-transparent shadow-none data-[state=on]:bg-transparent';
const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
});
export interface BiobookPageProps {
  biobookPage: IBiobookPage;
  className?: string;
  readOnly?: boolean;
  showBodyLifeMapLabels?: boolean;
  showManagementLabel?: boolean;
  showMatchedConditions?: boolean;
  showMechanismLabel?: boolean;
  tooltipContainer?: HTMLDivElement | null;
  style?: CSSProperties;
  userId: number;
}
export const BiobookPage = ({
  biobookPage,
  className,
  readOnly,
  showBodyLifeMapLabels = false,
  showManagementLabel = true,
  showMatchedConditions = false,
  showMechanismLabel = true,
  tooltipContainer,
  style,
  userId,
}: BiobookPageProps) => {
  const id = CSS.escape(`biobook-page-${biobookPage.phraseId}`);
  const element = useRef<HTMLDivElement | null>(null);
  const {data: biotwin} = useGetBiotwinQuery({
    id: userId,
  });
  const {data: biobookPreferences = defaultBiobookPreferences} = useGetBiobookPreferencesQuery({
    userId,
  });
  const [galleryDialogVisible, setGalleryDialogVisible] = useState(false);
  const [setBiobookPreferences] = useSetBiobookPreferencesMutation();
  const {favoritePhraseIds = [], hiddenPhraseIds = []} = biobookPreferences ?? defaultBiobookPreferences;
  const favorite = favoritePhraseIds.includes(biobookPage.phraseId);
  const hidden = hiddenPhraseIds.includes(biobookPage.phraseId);
  const [activeAbbreviationShortName, setActiveAbbreviationShortName] = useState('');
  const testModeEnabled = useConfigValue('testModeEnabled');
  const mechanismLabelColor = biobookPage.mechanismLabelColor ?? defaultMechanismLabelColor;
  const orderedFileNames = getOrderedBiobookPageFileNames(biobookPage);
  return (
    <div
      className={cn(styles.biobookPage, className, 'rounded-xl')}
      data-phraseid={biobookPage.phraseId}
      id={id}
      ref={element}
      style={style}
    >
      <Panel
        className="shadow-xs rounded-xl border border-zinc-300"
        bodyClassName="pt-0"
        footer={
          <div className="bg-zinc-200 py-2 px-4 -m-4 rounded-b-xl">
            <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-4 text-sm">
              <span
                className="bg-white rounded-3xl py-1 px-2"
                style={{
                  border: '1px solid currentColor',
                  color: biobookPage.phraseTypeColor,
                }}
              >
                {biobookPage.phraseTypeName}
              </span>
              <span className="whitespace-nowrap text-muted-foreground">
                <Icon icon="box" className="inline-flex h-fit text-sm" /> {biobookPage.moduleName}
              </span>
              <div className="flex flex-wrap items-baseline justify-end gap-1 md:ml-auto text-sm text-blue-400">
                <span id={`${id}-source`}>
                  <Icon
                    className="text-sm relative"
                    icon="info-circle"
                    style={{
                      top: 1,
                    }}
                  />
                  <ContentHints side="top" target={`#${id}-source`}>
                    Source: {biobookPage.sourceTitle}
                  </ContentHints>
                </span>{' '}
                {biobookPage.institutionShortNames.map((institutionShortName, index) => {
                  const elementId = CSS.escape(`${id}-${institutionShortName}`);
                  return (
                    <span id={elementId} key={index}>
                      {index > 0 && ', '} {institutionShortName}
                      <ContentHints side="top" target={`#${elementId}`}>
                        {biobookPage.institutionLongNames[index]}
                      </ContentHints>
                    </span>
                  );
                })}{' '}
                (
                {dateTimeFormat
                  .formatToParts(new Date(biobookPage.sourcePublishDate))
                  .map((part) => part.value)
                  .filter((value) => !!value.trim())
                  .join(', ')}
                )
                <a
                  className="ml-1"
                  href={biobookPage.sourceUrl}
                  id={`${id}-source-link`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Icon
                    className={cn('text-sm', {
                      'text-green-500': biobookPage.isDistantRelative,
                    })}
                    icon="external-link"
                  />
                  <ContentHints side="top" target={`#${id}-source-link`}>
                    Go to source
                  </ContentHints>
                </a>
              </div>
            </div>
          </div>
        }
        header={
          <div className="pt-4 px-4 rounded-t-xl">
            {(showBodyLifeMapLabels || showMechanismLabel || showManagementLabel) && (
              <div
                className="relative flex items-center justify-center gap-4 w-fit"
                title={testModeEnabled ? `#${biobookPage.phraseId}` : undefined}
              >
                <div
                  className={'absolute top-1/2 left-0 right-0'}
                  style={{
                    backgroundColor: mechanismLabelColor,
                    height: 1,
                  }}
                />
                {showBodyLifeMapLabels && (
                  <span
                    className="relative inline-flex items-center gap-1 text-xs p-1 rounded-md"
                    style={{
                      backgroundColor: mechanismLabelColor,
                    }}
                  >
                    <Icon icon={'person-fill'} />
                    {biobookPage.bodyLifeMapLabelNames?.join(', ')}
                  </span>
                )}
                {showMechanismLabel &&
                  biobookPage.mechanismLabelName !== 'No mechanism' &&
                  biobookPage.mechanismLabelName && (
                    <span
                      className="relative inline-flex items-center gap-1 text-xs p-1 rounded-md"
                      style={{
                        backgroundColor: mechanismLabelColor,
                      }}
                    >
                      <Icon icon={'pentagon-half'} />
                      {biobookPage.mechanismLabelName}
                    </span>
                  )}
                {showManagementLabel && biobookPage.managementLabelName && (
                  <span
                    className="relative inline-flex items-center gap-1 text-xs p-1 rounded-md"
                    style={{
                      backgroundColor: mechanismLabelColor,
                    }}
                  >
                    {biobookPage.managementLabelName.startsWith('Act') && (
                      <Icon icon={'capsule-pill'} className={'ml-1'} />
                    )}
                    {biobookPage.managementLabelName.startsWith('Think') && <Icon icon={'question-square'} />}
                    {biobookPage.managementLabelName}
                  </span>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center md:flex-nowrap md:gap-4">
              {showMatchedConditions && biobookPage.matchedConditionNames != null && (
                <div className={'relative flex items-center justify-center gap-4'}>
                  <div
                    className={'absolute top-1/2 bg-gray-300 left-0 right-0 w-full'}
                    style={{
                      height: 1,
                    }}
                  />
                  {biobookPage.matchedConditionNames.map((matchedConditionName) => (
                    <div
                      className={'relative bg-gray-200 text-foreground rounded-md text-xs p-1'}
                      key={matchedConditionName}
                    >
                      {matchedConditionName}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center ml-auto">
                {biotwin?.biobookUpdatePhraseIds?.includes(biobookPage.phraseId) && (
                  <div
                    className={'flex items-center mr-4'}
                    style={{
                      height: 0,
                    }}
                  >
                    <CertifiedBadge className={styles.newBadge} label={'NEW'} />
                  </div>
                )}
                {!readOnly && (
                  <>
                    <ToggleField
                      className={toggleClassName}
                      checked={favorite}
                      disabled={hidden}
                      offIcon="bookmark"
                      offLabel=""
                      onCheckedChange={() => {
                        setBiobookPreferences({
                          biobookPreferences: {
                            ...defaultBiobookPreferences,
                            ...biobookPreferences,
                            favoritePhraseIds: favorite
                              ? favoritePhraseIds.filter((phraseId) => phraseId !== biobookPage.phraseId)
                              : [...favoritePhraseIds, biobookPage.phraseId],
                          },
                          userId,
                        });
                      }}
                      onIcon="bookmark-fill"
                      onLabel=""
                      hint={favorite ? 'Remove From Bookmarks' : 'Add To Bookmarks'}
                    />
                    <ToggleField
                      className={toggleClassName}
                      checked={hidden}
                      disabled={favorite}
                      offIcon="eye-slash"
                      offLabel=""
                      onCheckedChange={() => {
                        setBiobookPreferences({
                          biobookPreferences: {
                            ...defaultBiobookPreferences,
                            ...biobookPreferences,
                            hiddenPhraseIds: hidden
                              ? hiddenPhraseIds.filter((phraseId) => phraseId !== biobookPage.phraseId)
                              : [...hiddenPhraseIds, biobookPage.phraseId],
                          },
                          userId,
                        });
                      }}
                      onIcon="eye"
                      onLabel=""
                      hint={hidden ? 'Show' : 'Hide'}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        }
      >
        {orderedFileNames.length > 0 && (
          <div className="flex items-center gap-4 mb-4">
            {orderedFileNames.slice(0, visibleImageCount).map((fileName, index) => (
              <BiobookPageImage
                biobookPage={biobookPage}
                figCaption={`Fig. ${index + 1}`}
                fileName={fileName}
                key={fileName}
                onClick={() => setGalleryDialogVisible(true)}
              />
            ))}
            {orderedFileNames.length > visibleImageCount && (
              <div
                className={'cursor-pointer rounded-full hover:bg-gray-200 p-6 mb-6'}
                onClick={() => setGalleryDialogVisible(true)}
                tabIndex={0}
              >
                <span className={'text-2xl'}>+ {orderedFileNames.length - visibleImageCount}</span>
              </div>
            )}
          </div>
        )}
        <Modal
          className={cn(styles.galleryDialog, 'overflow-hidden')}
          contentClassName="p-0 flex flex-col"
          // The gallery fills the dialog; only the close button floats over its top-right corner.
          headerClassName="sr-only"
          onClose={() => setGalleryDialogVisible(false)}
          style={{
            height: 'calc(100% - 2rem)',
            width: 'calc(100% - 2rem)',
          }}
          open={galleryDialogVisible}
        >
          <BiobookPageGallery biobookPage={biobookPage} className={'min-h-0 h-full'} />
        </Modal>
        <div
          dangerouslySetInnerHTML={{
            __html: biobookPage.phraseBodyHtml,
          }}
        />
        <ContentHints
          shouldHide={() => setActiveAbbreviationShortName('')}
          shouldShow={(e) => setActiveAbbreviationShortName(e.target.innerText)}
          style={{
            marginTop: tooltipContainer?.scrollTop ?? 0,
          }}
          target={`#${id} .b-abbr`}
        >
          {activeAbbreviationShortName && <AbbreviationLongName shortName={activeAbbreviationShortName} />}
        </ContentHints>
      </Panel>
    </div>
  );
};
